/**
 * What makes a market's move or level stand out against its own record: the last session that
 * moved as far the same way, or closed as far out, and how long ago that was. Chosen by rule, so
 * the ticker says only what its record shows; worded in `src/format/ticker.ts`.
 */
import { YEAR_SESSIONS } from './when.ts';

export interface DailyClose {
  day: string;
  close: number;
}

export const STANDOUT_KINDS = ['rise', 'fall', 'high', 'low'] as const;

export type StandoutKind = (typeof STANDOUT_KINDS)[number];

export interface Standout {
  kind: StandoutKind;
  /** The last session that went as far, or null where none in the record did. */
  since: string | null;
  /** Sessions back to `since`; where none went as far, every session of the record. */
  sessions: number;
}

/** A move in percent of its close, or in the record's own points, as a yield moves. */
export type Measure = 'fraction' | 'points';

/** With fewer moves of record than this, nothing is judged. */
const RECORD_MIN = 20;

/** A move is said where none as large the same way came in the month before. */
const MOVE_SESSIONS = 20;

/** A level, where nothing closed as far out in the quarter before. */
const LEVEL_SESSIONS = 60;

const moveOf = (from: number, to: number, measure: Measure): number =>
  measure === 'points' ? to - from : (to - from) / from;

interface Session {
  day: string;
  value: number;
}

/** The newest session `found` holds for, counting the newest as one session back. */
function lastWhere(
  sessions: readonly Session[],
  found: (value: number) => boolean,
): Omit<Standout, 'kind'> {
  for (let back = 1; back <= sessions.length; back += 1) {
    const session = sessions[sessions.length - back];
    if (session !== undefined && found(session.value))
      return { since: session.day, sessions: back };
  }
  return { since: null, sessions: sessions.length };
}

/**
 * `closes` oldest first, each a session before `last`'s, the newest the close `last` moves from.
 * The level comes first, as a year's high says more than a month's move.
 */
export function standoutsOf(
  closes: readonly DailyClose[],
  last: number,
  measure: Measure,
): Standout[] {
  const record = closes.slice(-(YEAR_SESSIONS + 1));
  const reference = record.at(-1)?.close;
  if (reference === undefined || reference <= 0 || record.length <= RECORD_MIN) return [];
  const found: Standout[] = [];

  const levels = record.map(({ day, close }) => ({ day, value: close }));
  const higher = lastWhere(levels, (close) => close >= last);
  const lower = lastWhere(levels, (close) => close <= last);
  if (higher.sessions > LEVEL_SESSIONS) found.push({ kind: 'high', ...higher });
  else if (lower.sessions > LEVEL_SESSIONS) found.push({ kind: 'low', ...lower });

  const move = moveOf(reference, last, measure);
  if (move === 0) return found;
  // Each move is the later day's of its pair, so the record holds one fewer moves than closes.
  const moves = record.flatMap(({ day, close }, index) => {
    const from = record[index - 1];
    return from === undefined ? [] : [{ day, value: moveOf(from.close, close, measure) }];
  });
  const larger = lastWhere(
    moves,
    (then) => Math.sign(then) === Math.sign(move) && Math.abs(then) >= Math.abs(move),
  );
  if (larger.sessions > MOVE_SESSIONS) found.push({ kind: move > 0 ? 'rise' : 'fall', ...larger });
  return found;
}
